import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of } from 'rxjs';

import { TicketingConfigApiService } from '../../features/administration/ticketing-config/ticketing-config-api.service';
import { TicketingApiService } from '../../features/ticketing/ticketing-api.service';
import { ConversationsApiService } from '../../features/conversations/conversations-api.service';

export interface GlobalSearchResult {
  kind: 'Ticket' | 'Conversation' | 'Department' | 'Queue' | 'Category';
  id: string;
  label: string;
  sublabel: string;
  routerLink: string[];
}

/**
 * Agent Workspace phase's Global Search Foundation. Federated, not a single backend search
 * index -- there is no unified search API in this platform (see the implementation report's
 * Search section), so this queries each module's own existing endpoint and merges the
 * results client-side:
 *  - Tickets: GetTicketsQuery's new SearchText filter (real server-side text search).
 *  - Conversations: Communication Platform v2's SearchConversationsQuery (real server-side
 *    text search across messages, attachments, tags, participants, and internal notes --
 *    see its own remarks on why ticket number isn't a separate dimension here: the Tickets
 *    arm above already reaches ticket-number matches, and Ticket carries its own
 *    Conversation link).
 *  - Departments/Queues/Categories: their existing GET-all endpoints, filtered client-side --
 *    these lists are small (tens, not thousands, of rows per organization), so a client
 *    filter over a cached full list is a reasonable, honest simplification.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly ticketingApi = inject(TicketingApiService);
  private readonly configApi = inject(TicketingConfigApiService);
  private readonly conversationsApi = inject(ConversationsApiService);

  search(query: string): Observable<GlobalSearchResult[]> {
    const trimmed = query.trim();

    if (trimmed.length < 2) {
      return of([]);
    }

    return forkJoin({
      tickets: this.ticketingApi.search({ searchText: trimmed, pageSize: 5 }),
      conversations: this.conversationsApi.search(trimmed, 1, 5),
      departments: this.configApi.getDepartments(false),
      queues: this.configApi.getQueues(false),
      categories: this.configApi.getCategories(false),
    }).pipe(
      map(({ tickets, conversations, departments, queues, categories }) => {
        const lower = trimmed.toLowerCase();

        const results: GlobalSearchResult[] = tickets.items.map((t) => ({
          kind: 'Ticket',
          id: t.ticketId,
          label: `#${t.ticketNumber} · ${t.subject}`,
          sublabel: `${t.status} · ${t.priority}`,
          routerLink: ['/tickets', t.ticketId],
        }));

        for (const conversation of conversations.items) {
          results.push({
            kind: 'Conversation',
            id: conversation.conversationId,
            label: conversation.customerDisplayName || conversation.customerPhoneNumber,
            sublabel: `${conversation.status} · matched in ${conversation.matchedIn.join(', ')}`,
            routerLink: ['/conversations', conversation.conversationId],
          });
        }

        for (const department of departments) {
          if (department.name.toLowerCase().includes(lower)) {
            results.push({
              kind: 'Department',
              id: department.departmentId,
              label: department.name,
              sublabel: 'Department',
              routerLink: ['/administration/ticketing-config'],
            });
          }
        }

        for (const queue of queues) {
          if (queue.name.toLowerCase().includes(lower)) {
            results.push({
              kind: 'Queue',
              id: queue.queueId,
              label: queue.name,
              sublabel: 'Queue',
              routerLink: ['/administration/ticketing-config'],
            });
          }
        }

        for (const category of categories) {
          if (category.name.toLowerCase().includes(lower)) {
            results.push({
              kind: 'Category',
              id: category.categoryId,
              label: category.name,
              sublabel: 'Category',
              routerLink: ['/administration/ticketing-config'],
            });
          }
        }

        return results.slice(0, 20);
      }),
    );
  }
}
